/**
 * Three worked examples, to show what a challenge actually looks like.
 *
 * The hard rule here is that nobody is invented. Every name comes from
 * `/api/challenges/people`, which is the real membership; when the viewer is
 * the only person signed up, the examples drop the opponent and address them
 * directly rather than filling the gap with a stock name. A room of nine
 * friends notices a "Dave" who does not exist immediately, and it makes the
 * whole screen read as a demo.
 *
 * The sentences are the real templates from `@shared/templates` (D63), so
 * these teach the format rather than a prettier fiction of it. Stakes are
 * pushups and laps, because the app does not take money (D65) and an example
 * is exactly where someone would get the wrong idea.
 *
 * Tapping one opens the real composer. It does not prefill: the picker needs a
 * game, a side and a stake the person actually means, and a half-filled form
 * they have to undo is worse than an empty one they fill in.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PublicUser } from '@shared/types'
import { teamAbbr } from '@shared/teams'
import { GET } from '../lib/api'
import { useStore } from '../lib/store'
import { Eyebrow } from './index'

interface PeopleResponse {
  here: PublicUser[]
  others: PublicUser[]
}

export interface ExampleSeed {
  /** Two team abbreviations for the game these examples sit against. */
  home?: string
  away?: string
}

export function ExampleChallenges({ home, away, style }: ExampleSeed & { style?: React.CSSProperties }) {
  const navigate = useNavigate()
  const { state } = useStore()
  const meId = state?.user.id ?? ''
  const [names, setNames] = useState<string[]>([])

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const res = await GET<PeopleResponse>('/api/challenges/people', ac.signal)
        const all = [...(res.here ?? []), ...(res.others ?? [])]
        const seen = new Set<string>()
        const usable: string[] = []
        for (const p of all) {
          if (p.id === meId || seen.has(p.id) || !p.displayName) continue
          seen.add(p.id)
          usable.push(p.displayName)
          if (usable.length === 2) break
        }
        setNames(usable)
      } catch {
        // No names is a supported state, not an error. The examples still work.
      }
    })()
    return () => ac.abort()
  }, [meId])

  const myTeam = state?.user.teamId ? teamAbbr(state.user.teamId) : null
  const teamA = away ?? myTeam ?? 'Your team'
  const teamB = home ?? 'the other side'

  const lines: { text: string; stake: string; who: string }[] = [
    {
      text: `${teamA} will be the first team to score.`,
      stake: '20 pushups',
      who: names[0] ? `You vs ${names[0]}` : 'You vs whoever takes it',
    },
    {
      text: `${teamA} will be leading at halftime.`,
      stake: '30 pushups',
      who: names[1] ? `You vs ${names[1]}` : 'Open to the room',
    },
    {
      text: `${teamA} and ${teamB} will go to overtime.`,
      stake: '2 laps of the block',
      who: 'Open to the room',
    },
  ]

  return (
    <section style={style}>
      <Eyebrow>Examples</Eyebrow>
      {/* The disclaimer under this heading is gone. "Not real. This is the
          shape of one." was explaining a joke: nobody reading three sample
          feats under the word Examples thought Dave had actually agreed to
          them, and the line made the section feel like a manual. */}
      <div style={{ marginTop: 10 }}>
        {lines.map((l, i) => (
          <button
            key={l.text}
            onClick={() => navigate('/challenges/new')}
            style={{
              display: 'block', width: '100%', textAlign: 'left', minHeight: 44,
              padding: '13px 14px', marginTop: i === 0 ? 0 : 8,
              borderRadius: 14, border: '1px dashed var(--line)',
              background: 'transparent', color: 'var(--fg)',
            }}
          >
            <div style={{ fontSize: 14, lineHeight: 1.45 }}>{l.text}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', marginTop: 6 }}>
              {l.who} · {l.stake}
            </div>
          </button>
        ))}
      </div>
    </section>
  )
}
